import React, { useState, useEffect, createContext, useContext } from "react";
import { useCookies } from "react-cookie";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import Layout from "./components/DashboardLayout/Layout";
import AppRoutes from "./routes/Routes";

const AuthContext = createContext();

const App = () => {
  const [cookies, setCookie, removeCookie] = useCookies(["auth_token"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initializeAuth = () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");

      if (token) {
        try {
          const decoded = jwtDecode(token);

          // Set the token in cookies
          setCookie("auth_token", token, {
            path: "/",
            secure: process.env.NODE_ENV === "production",
            sameSite: "Strict",
          });

          // Set current user
          setCurrentUser(decoded);

          // Clean the URL
          window.history.replaceState({}, document.title, "/");
        } catch (err) {
          console.error("Invalid token in URL:", err);
          // handleLogout();
        }
      } else if (cookies.auth_token) {
        try {
          const decoded = jwtDecode(cookies.auth_token);
          setCurrentUser(decoded);
        } catch (err) {
          console.error("Invalid token in cookies:", err);
          // handleLogout();
        }
      } else {
        navigate("/login");
      }
      setAuthLoading(false); // Auth initialization complete
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      refreshToken();
    }, 4 * 60 * 60 * 1000); // every 4 hours

    return () => clearInterval(interval);
  }, [currentUser]);

  const refreshToken = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (res.ok) {
        const data = await res.json();
        const newToken = data.token;

        setCookie("auth_token", newToken, {
          path: "/",
          secure: process.env.NODE_ENV === "production",
          sameSite: "Strict",
        });

        const decoded = jwtDecode(newToken);
        setCurrentUser(decoded);

        console.info("Token refreshed proactively.");
      } else {
        console.error("Failed to refresh token proactively.");
        handleLogout();
      }
    } catch (err) {
      console.error("Error refreshing token proactively:", err);
      handleLogout();
    }
  };

  const handleLogout = () => {
    removeCookie("auth_token", { path: "/" });
    setCurrentUser(null);
    navigate("/login");
  };

  if (authLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ currentUser, logout: handleLogout }}>
      <Layout currentUser={currentUser}>
        <AppRoutes />
      </Layout>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default App;
